import { useState, useCallback, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { chatStream } from '../../hooks/useAI';
import type { ChatMessage } from '../../hooks/useAI';

interface AIStreamingTextProps {
  messages: ChatMessage[];
  taskType?: string;
  onComplete?: (fullContent: string) => void;
  onError?: (error: string) => void;
  className?: string;
}

export default function AIStreamingText({ messages, taskType, onComplete, onError, className }: AIStreamingTextProps) {
  const [content, setContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    abortRef.current = new AbortController();

    const runStream = async () => {
      try {
        let fullContent = '';
        const generator = chatStream(messages, taskType, abortRef.current?.signal);

        for await (const chunk of generator) {
          if (!mountedRef.current) return;
          fullContent += chunk;
          setContent(fullContent);
        }

        if (mountedRef.current) {
          setIsStreaming(false);
          onComplete?.(fullContent);
        }
      } catch (err: any) {
        if (!mountedRef.current) return;
        if (err.name === 'AbortError') return;
        const msg = err.message || 'Streaming failed';
        setError(msg);
        setIsStreaming(false);
        onError?.(msg);
      }
    };

    runStream();

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [messages, taskType]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="prose prose-sm max-w-none prose-p:my-2 prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        {isStreaming && (
          <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse ml-0.5" />
        )}
      </div>
      {isStreaming && (
        <button
          onClick={stop}
          className="mt-2 text-xs text-gray-500 hover:text-gray-700"
        >
          Stop generating
        </button>
      )}
    </div>
  );
}
